import {
  DEFAULT_PREFERENCES,
  canSavePreferencesForPlan,
  getRequiredPlanForPreferences,
  normalizePreferences,
} from "./preferences.js";
import {
  getPlanLabel,
  normalizePlan,
} from "./plans.js";

const ENV = import.meta.env ?? {};

const ENV_REQUESTS_ONBOARDING_DESIGN_MODE =
  ["1", "true", "yes", "on"].includes(
    String(ENV.VITE_ONBOARDING_DESIGN_MODE ?? "").trim().toLowerCase(),
  );

const DESIGN_MODE_MIN_NUMBER =
  String(ENV.VITE_ONBOARDING_DESIGN_MIN_NUMBER ?? "27686044011").trim() || "27686044011";

const STATE_KEY = "mincool.onboardingDesignState";
const LINK_STATE_KEY = "mincool.onboardingDesignLinkState";
const ONBOARDING_INCLUDED_PLAN = "fun";
const DEFAULT_ONBOARDING_PREFERENCES = Object.freeze({
  ...DEFAULT_PREFERENCES,
  inboundSummary: true,
  inboundTranscript: true,
  outboundSummary: true,
  outboundTranscript: false,
});

export const DESIGN_STEP_VALUES = ["success", "email-sent", "verified", "pricing", "preferences", "message"];
export const DESIGN_USER_VALUES = ["new", "existing"];

const DEFAULT_STATE = Object.freeze({
  auth: {
    status: "idle",
    email: "",
    firstName: "",
    lastName: "",
    passwordDraft: "",
    userMode: "new",
  },
  billing: {
    cadence: "annual",
    selectedPlan: "",
    purchasedPlan: null,
  },
  onboardingCompletedAt: null,
  preferences: DEFAULT_ONBOARDING_PREFERENCES,
});

function canUseWindow() {
  return typeof window !== "undefined";
}

export function isLocalDesignModeHost(hostname) {
  const normalized = String(hostname || "").trim().toLowerCase();
  if (!normalized) {
    return false;
  }
  return (
    normalized === "localhost"
    || normalized === "::1"
    || normalized.endsWith(".local")
    || /^127\./.test(normalized)
    || /^10\./.test(normalized)
    || /^192\.168\./.test(normalized)
    || /^172\.(1[6-9]|2\d|3[0-1])\./.test(normalized)
  );
}

export function isDesignModeHost(hostname) {
  const normalized = String(hostname || "").trim().toLowerCase();
  return isLocalDesignModeHost(normalized) || normalized.endsWith(".vercel.app");
}

function currentHostAllowsDesignMode() {
  if (!canUseWindow()) {
    return false;
  }
  return isDesignModeHost(window.location?.hostname);
}

export function shouldUseLocalOnboardingPreview({ hostname = "", search = "" } = {}) {
  return isLocalDesignModeHost(hostname) && Boolean(readOnboardingDesignParams(search).demoStep);
}

export function currentRequestAllowsLocalOnboardingPreview(search = "") {
  if (!canUseWindow()) {
    return false;
  }
  return shouldUseLocalOnboardingPreview({
    hostname: window.location?.hostname,
    search,
  });
}

export const ONBOARDING_DESIGN_MODE =
  ENV_REQUESTS_ONBOARDING_DESIGN_MODE && currentHostAllowsDesignMode();

function cloneDefaultState() {
  return {
    auth: { ...DEFAULT_STATE.auth },
    billing: { ...DEFAULT_STATE.billing },
    onboardingCompletedAt: DEFAULT_STATE.onboardingCompletedAt,
    preferences: { ...DEFAULT_ONBOARDING_PREFERENCES },
  };
}

function getSessionStorage() {
  if (!canUseWindow()) {
    return null;
  }
  return window.sessionStorage;
}

function mergeState(partial) {
  const selectedPlan = normalizePlan(partial?.billing?.selectedPlan, "");
  return {
    auth: {
      ...DEFAULT_STATE.auth,
      ...(partial?.auth ?? {}),
    },
    billing: {
      ...DEFAULT_STATE.billing,
      ...(partial?.billing ?? {}),
      selectedPlan: ["free", "fun", "pro"].includes(selectedPlan) ? selectedPlan : "",
      purchasedPlan: partial?.billing?.purchasedPlan
        ? normalizePlan(partial.billing.purchasedPlan)
        : null,
      cadence: partial?.billing?.cadence === "monthly" ? "monthly" : "annual",
    },
    onboardingCompletedAt:
      typeof partial?.onboardingCompletedAt === "string" && partial.onboardingCompletedAt.trim()
        ? partial.onboardingCompletedAt.trim()
        : null,
    preferences: {
      ...DEFAULT_ONBOARDING_PREFERENCES,
      ...normalizePreferences(partial?.preferences ?? {}),
    },
  };
}

export function getOnboardingDesignState() {
  const storage = getSessionStorage();
  if (!storage) {
    return cloneDefaultState();
  }
  const raw = storage.getItem(STATE_KEY);
  if (!raw) {
    return cloneDefaultState();
  }
  try {
    return mergeState(JSON.parse(raw));
  } catch {
    return cloneDefaultState();
  }
}

export function updateOnboardingDesignState(updater) {
  const nextState = mergeState(
    typeof updater === "function" ? updater(getOnboardingDesignState()) : updater,
  );
  const storage = getSessionStorage();
  if (storage) {
    storage.setItem(STATE_KEY, JSON.stringify(nextState));
  }
  return nextState;
}

export function resetOnboardingDesignState() {
  const storage = getSessionStorage();
  if (storage) {
    storage.removeItem(STATE_KEY);
    storage.removeItem(LINK_STATE_KEY);
  }
  return cloneDefaultState();
}

export function readOnboardingDesignParams(search = "") {
  const params = new URLSearchParams(search);
  const demoStep = String(params.get("demoStep") || "").trim().toLowerCase();
  const demoPlan = normalizePlan(params.get("demoPlan"), "");
  const demoUser = String(params.get("demoUser") || "").trim().toLowerCase();

  return {
    demoStep: DESIGN_STEP_VALUES.includes(demoStep) ? demoStep : "",
    demoPlan: demoPlan || "",
    demoUser: DESIGN_USER_VALUES.includes(demoUser) ? demoUser : "",
  };
}

export function getOnboardingEffectivePlan(state, forcedPlan = "") {
  if (forcedPlan) {
    return normalizePlan(forcedPlan);
  }
  const purchasedPlan = normalizePlan(state?.billing?.purchasedPlan, "");
  if (purchasedPlan) {
    return purchasedPlan;
  }
  if (!state?.onboardingCompletedAt && String(state?.auth?.userMode || "new") !== "existing") {
    return ONBOARDING_INCLUDED_PLAN;
  }
  return "free";
}

export function getPreferenceRows() {
  return [
    {
      key: "inboundSummary",
      label: "Inbound summaries",
      detail: "Summarize voice notes people send to you.",
      requiredPlan: "fun",
    },
    {
      key: "inboundTranscript",
      label: "Inbound transcripts",
      detail: "Read every incoming voice note in full.",
      requiredPlan: "fun",
    },
    {
      key: "outboundSummary",
      label: "Outbound summaries",
      detail: "Summarize the voice notes you send.",
      requiredPlan: "free",
    },
    {
      key: "outboundTranscript",
      label: "Outbound transcripts",
      detail: "Keep full text for the voice notes you send.",
      requiredPlan: "fun",
    },
    {
      key: "branding",
      label: "Min branding",
      detail: "Keep the min.cool footer on your outputs.",
      requiredPlan: "free",
      lockedWhenOff: "pro",
    },
  ];
}

export function getOnboardingPromoPlans(cadence = "annual") {
  const normalizedCadence = cadence === "monthly" ? "monthly" : "annual";
  const annual = normalizedCadence === "annual";

  return [
    {
      slug: "fun",
      name: "Fun",
      headlinePrice: annual ? "$0.10" : "$0.50",
      originalHeadlinePrice: "$1",
      suffix: "/mo",
      billedNow: annual ? "$1.20 upfront" : "$0.50 billed monthly",
      promoLabel: annual ? "90% off for 12 months" : "50% off for 6 months",
      caption: "Adds full transcripts without jumping all the way to Pro.",
      selectable: true,
      statusLabel: "",
    },
    {
      slug: "pro",
      name: "Pro",
      headlinePrice: annual ? "$1" : "$5",
      originalHeadlinePrice: "$10",
      suffix: "/mo",
      billedNow: annual ? "$12 upfront" : "$5 billed monthly",
      promoLabel: annual ? "90% off for 12 months" : "50% off for 6 months",
      caption: "Adds branding removal and per-chat controls.",
      selectable: true,
      statusLabel: "",
    },
    {
      slug: "agent",
      name: "Agent",
      headlinePrice: annual ? "$10" : "$50",
      originalHeadlinePrice: "$100",
      suffix: "/mo",
      billedNow: "Not available yet",
      promoLabel: "",
      caption: "The higher-touch Min tier is coming soon.",
      selectable: false,
      statusLabel: "Coming soon",
    },
  ];
}

export function getOnboardingPromoCadenceOptions(selectedCadence = "annual") {
  const normalizedCadence = selectedCadence === "monthly" ? "monthly" : "annual";

  return [
    {
      value: "annual",
      label: "Annual 90% off",
      badge: "Recommended",
      selected: normalizedCadence === "annual",
    },
    {
      value: "monthly",
      label: "Monthly 50% off",
      badge: "",
      selected: normalizedCadence === "monthly",
    },
  ];
}

export function getDesignModeMinWhatsappNumber() {
  return DESIGN_MODE_MIN_NUMBER;
}

export {
  DEFAULT_PREFERENCES,
  canSavePreferencesForPlan,
  getPlanLabel,
  getRequiredPlanForPreferences,
};

function getLinkState() {
  const storage = getSessionStorage();
  if (!storage) {
    return null;
  }
  const raw = storage.getItem(LINK_STATE_KEY);
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function setLinkState(nextState) {
  const storage = getSessionStorage();
  if (storage) {
    storage.setItem(LINK_STATE_KEY, JSON.stringify(nextState));
  }
  return nextState;
}

export function createMockLinkingSession() {
  return setLinkState({
    linkSessionId: "design-link-session",
    accountId: "design-account",
    status: "awaiting_pairing_code",
    polls: 0,
    phoneNumber: "",
  });
}

export function requestMockPairingCode(phoneNumber) {
  const current = getLinkState() || createMockLinkingSession();
  setLinkState({
    ...current,
    status: "awaiting_link",
    polls: 0,
    phoneNumber,
  });
  return {
    status: "awaiting_link",
    normalizedPhoneNumber: phoneNumber,
    pairingCode: "123 456",
    code: "123 456",
  };
}

export function pollMockLinkStatus() {
  const current = getLinkState() || createMockLinkingSession();
  const polls = Number(current.polls || 0) + 1;
  const nextStatus = current.status === "awaiting_link" && polls >= 2 ? "linked" : current.status;
  const nextState = setLinkState({
    ...current,
    polls,
    status: nextStatus,
  });

  return {
    linkSessionId: nextState.linkSessionId,
    status: nextStatus,
    method: "pairing_code",
    account: {
      id: nextState.accountId,
      plan: "free",
    },
  };
}

export function getMockQrPayload() {
  const svg = encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="280" height="280" viewBox="0 0 280 280">
      <rect width="280" height="280" rx="28" fill="#ffffff"/>
      <rect x="24" y="24" width="232" height="232" rx="20" fill="#0f172a"/>
      <rect x="52" y="52" width="176" height="176" rx="16" fill="#ffffff"/>
      <text x="140" y="126" text-anchor="middle" font-size="18" font-family="system-ui" fill="#0f172a">min.cool</text>
      <text x="140" y="156" text-anchor="middle" font-size="16" font-family="system-ui" fill="#475569">demo QR</text>
    </svg>`,
  );
  return {
    qrCode: `data:image/svg+xml;utf8,${svg}`,
  };
}
